import { SETTINGS_KEY } from "../../config";
import { isStorableName, isValidName, sanitizeName } from "../../utils/name";

interface StoredSettings {
  muted?: boolean;
  playerName?: string;
  authToken?: string;
}

export class Settings {
  muted: boolean;
  playerName: string;
  authToken: string;

  constructor(private readonly storage: Storage = window.localStorage) {
    const data = this.load();
    this.muted = data.muted ?? false;
    const stored = data.playerName ?? "";
    // Keep legacy spaced names so the migration flow can rename them; only a
    // genuinely malformed value is dropped.
    this.playerName = isStorableName(stored) ? stored : "";
    this.authToken = this.playerName && typeof data.authToken === "string" ? data.authToken : "";
  }

  setMuted(value: boolean): void {
    this.muted = value;
    this.save();
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  /** Returns the sanitized name that was actually stored, or "" if rejected. */
  setPlayerName(name: string): string {
    const clean = sanitizeName(name);
    if (!isValidName(clean)) {
      this.playerName = "";
      this.authToken = "";
      this.save();
      return "";
    }
    this.playerName = clean;
    this.save();
    return clean;
  }

  setAuth(name: string, token: string): void {
    const clean = sanitizeName(name);
    if (!isValidName(clean) || !token) {
      this.playerName = "";
      this.authToken = "";
    } else {
      this.playerName = clean;
      this.authToken = token;
    }
    this.save();
  }

  logout(): void {
    this.playerName = "";
    this.authToken = "";
    this.save();
  }

  hasName(): boolean {
    return this.playerName.length > 0;
  }

  isLoggedIn(): boolean {
    return this.playerName.length > 0 && this.authToken.length > 0;
  }

  private load(): StoredSettings {
    try {
      const raw = this.storage.getItem(SETTINGS_KEY);
      if (!raw) return {};
      return JSON.parse(raw) as StoredSettings;
    } catch {
      return {};
    }
  }

  private save(): void {
    try {
      this.storage.setItem(
        SETTINGS_KEY,
        JSON.stringify({
          muted: this.muted,
          playerName: this.playerName,
          authToken: this.authToken,
        }),
      );
    } catch {
      // ignore
    }
  }
}
